import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  ChevronLeft,
  Loader2,
  MapPin,
  Plus,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import { hitGetServiceAreaApi } from "../../../Redux/Professional/GetServiceAreasListProSlice";
import { hitTryAgainPaymentForServiceAreaSubscriptionApi } from "../../../Redux/Professional/TryAgainServiceAreaSubscriptionPaymentSlice";
import {
  ApiBaseUrl,
  areaSubScriptionPriceId,
} from "../../../Utils/Constants";
import { showToast } from "../../../Helper/Toast";
import { useGlobalState } from "../../../Providers/GlobalStateProvider";
import GoogleMapsPlacesProvider from "../../Common/GoogleMaps/GoogleMapsPlacesProvider";
import { MapComponent } from "../../Common/ServiceAreaMap/MapComponent";
import Loader from "../../Loader/Loader";
import "./ServiceAreas.scss";

const PRICE_PER_AREA = 9;

const AREA_PACKAGES = [
  { id: 1, areas: 3, price: 27 },
  { id: 2, areas: 7, price: 63, popular: true },
  { id: 3, areas: 10, price: 90 },
];

const formatAreaLabel = (area) => {
  if (!area) return "";
  if (area.city && area.state) return `${area.city}, ${area.state}`;
  return area.address || area.city || "Service area";
};

const extractZipFromComponents = (components = []) => {
  let zip = "";
  components.forEach((comp) => {
    if (comp.types.includes("postal_code")) {
      zip = comp.long_name;
    }
  });
  return zip;
};

const parseGeocodeResult = (result) => {
  let city = "";
  let state = "";
  let zip = extractZipFromComponents(result.address_components);
  result.address_components.forEach((comp) => {
    if (comp.types.includes("locality")) city = comp.long_name;
    if (!city && comp.types.includes("sublocality")) {
      city = comp.long_name;
    }
    if (comp.types.includes("administrative_area_level_1")) {
      state = comp.short_name || comp.long_name;
    }
  });
  return {
    city,
    state,
    zipCode: zip,
    placeId: result.place_id || "",
  };
};

const resolveZipCode = async (lat, lng, initialZip = "") => {
  if (initialZip) return initialZip;
  try {
    const reverseResults = await getGeocode({ location: { lat, lng } });
    for (const result of reverseResults) {
      const zip = extractZipFromComponents(result.address_components);
      if (zip) return zip;
    }
  } catch {
    /* fall through */
  }
  return "";
};

const buildServiceAreaPayload = (payload, existingArea = null) => {
  const body = {
    address: payload.address || existingArea?.address || "",
    city: payload.city || existingArea?.city || "",
    state: payload.state || existingArea?.state || "",
    zipCode:
      payload.zipCode ||
      payload.zip ||
      existingArea?.zipCode ||
      existingArea?.zip ||
      "",
    latitude: payload.latitude ?? existingArea?.latitude,
    longitude: payload.longitude ?? existingArea?.longitude,
    placeId: payload.placeId || existingArea?.placeId || "",
  };
  if (existingArea?._id) {
    body.serviceAreaId = existingArea._id;
  }
  return body;
};

const resolvePendingAddCount = (profile) =>
  Math.max(
    0,
    Number(profile?.totalServiceAreaCountPendingBasedOnPoints) || 0,
  );

// Empty add slots come only from pending points-based credit.
// If pending is 0, show existing areas only (no extra empty slots).
const resolveMaxSlots = (profile, areasLength) => {
  const pending = resolvePendingAddCount(profile);
  return areasLength + pending;
};

const SelectServiceAreaModal = ({ open, onClose, onSelect, isSaving }) => {
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      types: ["(cities)"],
      componentRestrictions: { country: "us" },
    },
    debounce: 300,
  });

  useEffect(() => {
    if (!open) {
      setValue("", false);
      clearSuggestions();
    }
  }, [open, setValue, clearSuggestions]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const handlePick = async (description) => {
    setValue(description, false);
    clearSuggestions();
    try {
      const results = await getGeocode({ address: description });
      if (!results?.length) return;
      const { lat, lng } = await getLatLng(results[0]);
      const parsed = parseGeocodeResult(results[0]);
      const zipCode = await resolveZipCode(lat, lng, parsed.zipCode);
      await onSelect({
        address: description,
        city: parsed.city,
        state: parsed.state,
        zipCode,
        latitude: lat,
        longitude: lng,
        placeId: parsed.placeId,
      });
    } catch {
      showToast("Could not resolve that location. Try again.", "error");
    }
  };

  if (!open) return null;

  const isSearching = value.trim().length > 0 && status === "LOADING";
  const hasQuery = value.trim().length > 0;
  const showEmptyResults =
    hasQuery && status !== "LOADING" && data.length === 0;

  return createPortal(
    <div className="pro-sa-select-overlay" onClick={onClose} role="presentation">
      <div
        className="pro-sa-select"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pro-sa-select-title"
      >
        <header className="pro-sa-select__header">
          <button
            type="button"
            className="pro-sa-select__back"
            onClick={onClose}
            aria-label="Close"
          >
            <ChevronLeft size={22} />
          </button>
          <h2 id="pro-sa-select-title">Select service area</h2>
        </header>

        <div className="pro-sa-select__search-wrap">
          <input
            type="text"
            className="pro-sa-select__search"
            placeholder="Select for a service area"
            value={value}
            disabled={!ready || isSaving}
            onChange={(e) => setValue(e.target.value)}
          />
          {isSearching && (
            <Loader2
              className="pro-sa-select__spinner"
              size={18}
              aria-hidden
            />
          )}
        </div>

        <div className="pro-sa-select__body">
          {!hasQuery && (
            <p className="pro-sa-select__hint">
              Add the city and state where you operate
            </p>
          )}

          {showEmptyResults && (
            <p className="pro-sa-select__empty">No results found</p>
          )}

          {data.length > 0 && (
            <ul className="pro-sa-select__results">
              {data.map((item) => (
                <li key={item.place_id}>
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={() => handlePick(item.description)}
                  >
                    {item.description}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const ServiceAreasInner = ({ mobileToken = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setGlobalLoading } = useGlobalState();

  const [loading, setLoading] = useState(true);
  const [areas, setAreas] = useState([]);
  const [profileMeta, setProfileMeta] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectOpen, setSelectOpen] = useState(false);
  const [activeSlot, setActiveSlot] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentRetry, setShowPaymentRetry] = useState(false);
  const [areaPendingDelete, setAreaPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [customAreas, setCustomAreas] = useState(1);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const authToken =
    mobileToken ||
    localStorage.getItem("professionalToken") ||
    null;

  const pendingAddCount = useMemo(
    () => resolvePendingAddCount(profileMeta),
    [profileMeta],
  );

  const maxSlots = useMemo(
    () => resolveMaxSlots(profileMeta, areas.length),
    [profileMeta, areas.length],
  );

  const slots = useMemo(() => {
    const list = Array.from({ length: maxSlots }, (_, index) => ({
      index,
      area: areas[index] || null,
    }));
    return list;
  }, [areas, maxSlots]);

  const selectedArea = areas[selectedIndex] || areas[0] || null;

  const loadAreas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dispatch(hitGetServiceAreaApi(authToken));
      const payload = response?.payload;
      const list = Array.isArray(payload?.data) ? payload.data : [];
      setAreas(list);
      setProfileMeta(payload?.profile || null);
      setShowPaymentRetry(
        Number(payload?.profile?.isLastServiceAreaSubscriptionPaymentFailed) ===
          1,
      );
      setSelectedIndex((prev) =>
        list.length > 0 && prev >= list.length ? 0 : prev,
      );
    } catch {
      showToast("Failed to load service areas", "error");
    } finally {
      setLoading(false);
      setGlobalLoading(false);
    }
  }, [authToken, dispatch, setGlobalLoading]);

  useEffect(() => {
    if (!authToken) {
      navigate(mobileToken ? "/login" : "/professional/login");
      return;
    }
    setGlobalLoading(true);
    loadAreas();
  }, [authToken, loadAreas, mobileToken, navigate, setGlobalLoading]);

  const saveArea = async (payload, slotIndex, existingArea = null) => {
    if (!payload.zipCode && !payload.zip) {
      showToast(
        "Could not determine zip code for this location. Try a more specific address.",
        "error",
      );
      return;
    }

    if (!existingArea?._id && pendingAddCount <= 0) {
      showToast(
        "No pending service areas available to add. Purchase more if needed.",
        "info",
      );
      return;
    }

    setIsSaving(true);
    setGlobalLoading(true);
    try {
      const body = buildServiceAreaPayload(payload, existingArea);

      const isUpdate = Boolean(existingArea?._id);
      const url = isUpdate
        ? `${ApiBaseUrl}v1/professional/updateServiceArea`
        : `${ApiBaseUrl}v1/professional/addServiceArea`;

      const response = await (isUpdate
        ? axios.put(url, body, {
            headers: {
              Authorization: authToken,
              "Content-Type": "application/json",
            },
          })
        : axios.post(url, body, {
            headers: {
              Authorization: authToken,
              "Content-Type": "application/json",
            },
          }));

      if (response.data?.status === true) {
        showToast(
          isUpdate ? "Service area updated" : "Service area added",
          "success",
        );
        setSelectOpen(false);
        setActiveSlot(null);
        await loadAreas();
        if (typeof slotIndex === "number") setSelectedIndex(slotIndex);
      } else {
        showToast(
          response.data?.message || "Could not save service area",
          "error",
        );
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Could not save service area. You may need to purchase additional areas.";
      showToast(message, "error");
      if (areas.length >= maxSlots) setShowPaymentModal(true);
    } finally {
      setIsSaving(false);
      setGlobalLoading(false);
    }
  };

  const requestDeleteArea = (area) => {
    if (!area?._id) return;
    setAreaPendingDelete(area);
  };

  const cancelDeleteArea = () => {
    if (isDeleting) return;
    setAreaPendingDelete(null);
  };

  const deleteArea = async () => {
    const area = areaPendingDelete;
    if (!area?._id) return;
    setIsDeleting(true);
    setGlobalLoading(true);
    try {
      const response = await axios.put(
        `${ApiBaseUrl}v1/professional/updateServiceArea`,
        {
          serviceAreaId: area._id,
          isDeleted: 1,
        },
        {
          headers: {
            Authorization: authToken,
            "Content-Type": "application/json",
          },
        },
      );
      if (response.data?.status === true) {
        showToast("Service area removed", "success");
        setAreaPendingDelete(null);
        await loadAreas();
        setSelectedIndex(0);
      } else {
        showToast(response.data?.message || "Could not remove area", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Could not remove service area",
        "error",
      );
    } finally {
      setIsDeleting(false);
      setGlobalLoading(false);
    }
  };

  const openAddForSlot = (slotIndex) => {
    const filledCount = areas.length;
    if (pendingAddCount <= 0) {
      showToast(
        "No pending service areas available to add. Purchase more if needed.",
        "info",
      );
      return;
    }
    if (slotIndex > filledCount) {
      showToast("Fill previous service areas first", "info");
      return;
    }
    if (filledCount >= maxSlots) {
      setShowPaymentModal(true);
      return;
    }
    setActiveSlot(slotIndex);
    setSelectOpen(true);
  };

  const handleSelectArea = async (locationPayload) => {
    const existing = typeof activeSlot === "number" ? areas[activeSlot] : null;
    await saveArea(locationPayload, activeSlot ?? areas.length, existing);
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    setGlobalLoading(true);
    try {
      const response = await axios.post(
        `${ApiBaseUrl}v1/professional/createStripeSessionSubscriptionServiceArea`,
        {
          priceId: areaSubScriptionPriceId,
          quantity: parseInt(customAreas, 10),
        },
        { headers: { Authorization: authToken } },
      );
      if (response.data?.status === true) {
        if (response.data.data?.url) {
          window.location.href = response.data.data.url;
        } else {
          showToast("Service area subscription updated", "success");
          setShowPaymentModal(false);
          await loadAreas();
        }
      } else {
        showToast("Payment could not be started. Please try again.", "error");
      }
    } catch {
      showToast("Payment could not be started. Please try again.", "error");
    } finally {
      setCheckoutLoading(false);
      setGlobalLoading(false);
    }
  };

  const openPurchaseModal = () => {
    setSelectedPkg(null);
    setCustomAreas(1);
    setShowPaymentModal(true);
  };

  const handleRetryPayment = async () => {
    setCheckoutLoading(true);
    setGlobalLoading(true);
    try {
      const response = await dispatch(
        hitTryAgainPaymentForServiceAreaSubscriptionApi(authToken),
      );
      if (response.payload?.status === true) {
        window.location.href = response.payload.data;
      } else {
        showToast("Payment failed. Please try again.", "error");
      }
    } catch {
      showToast("Payment failed. Please try again.", "error");
    } finally {
      setCheckoutLoading(false);
      setGlobalLoading(false);
    }
  };

  const handleBack = () => {
    if (mobileToken) {
      navigate(-1);
      return;
    }
    if (window.history.length > 1) navigate(-1);
    else navigate("/professional/settings");
  };

  if (loading) {
    return (
      <div
        className={[
          "pro-app-page",
          "pro-app-page--muted",
          "pro-sa-page",
          mobileToken ? "pro-sa-page--mobile" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="pro-sa-page__loader">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div
      className={[
        "pro-app-page",
        "pro-app-page--muted",
        "pro-sa-page",
        mobileToken ? "pro-sa-page--mobile" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="custom_container pro-sa-page__inner">
        <header className="pro-sa-page__hero">
          <button
            type="button"
            className="pro-sa-page__back"
            onClick={handleBack}
            aria-label="Go back"
          >
            <ChevronLeft size={24} />
          </button>
          <h1>Add Service Area(s)</h1>
        </header>

        <div className="pro-sa-banner">
          Service Areas: <strong>{areas.length}</strong> of{" "}
          <strong>{maxSlots}</strong> added
          {pendingAddCount > 0 ? (
            <>
              {" "}
              · <strong>{pendingAddCount}</strong> pending to add
            </>
          ) : null}
        </div>

        <div className="pro-sa-info">
          <span className="pro-sa-info__icon" aria-hidden>
            <Plus size={20} strokeWidth={2.5} />
          </span>
          <div>
            <h2>Operate in multiple areas?</h2>
            <p>
              Expand your service coverage for just ${PRICE_PER_AREA}/month per
              additional area. Add more cities to reach customers across
              different regions.
            </p>
          </div>
        </div>

        <section className="pro-sa-list-section">
          <h3>Your Service Areas ({areas.length})</h3>
          <ul className="pro-sa-slots">
            {slots.map(({ index, area }) => {
              const isActive = selectedIndex === index && area;
              const isFilled = Boolean(area);
              return (
                <li key={index}>
                  <div
                    className={[
                      "pro-sa-slot",
                      isActive && "pro-sa-slot--active",
                      !isFilled && "pro-sa-slot--empty",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <button
                      type="button"
                      className="pro-sa-slot__main"
                      onClick={() => {
                        if (area) setSelectedIndex(index);
                      }}
                    >
                      <span
                        className={[
                          "pro-sa-slot__badge",
                          isFilled
                            ? "pro-sa-slot__badge--filled"
                            : "pro-sa-slot__badge--empty",
                        ].join(" ")}
                      >
                        {index + 1}
                      </span>
                      <span className="pro-sa-slot__text">
                        <strong>
                          {isFilled
                            ? formatAreaLabel(area)
                            : `Area ${index + 1}`}
                        </strong>
                        {isFilled && <span>Zone {index + 1}</span>}
                      </span>
                    </button>

                    {isFilled ? (
                      <div className="pro-sa-slot__actions">
                        <button
                          type="button"
                          className="pro-sa-slot__edit"
                          onClick={() => {
                            setActiveSlot(index);
                            setSelectOpen(true);
                          }}
                        >
                          Edit
                        </button>
                        {index > 0 && (
                          <button
                            type="button"
                            className="pro-sa-slot__delete"
                            aria-label="Remove service area"
                            onClick={() => requestDeleteArea(area)}
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="pro-sa-slot__add"
                        aria-label={`Add area ${index + 1}`}
                        onClick={() => openAddForSlot(index)}
                      >
                        <Plus size={20} />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        {!showPaymentRetry && (
          <section className="pro-sa-purchase">
            <button
              type="button"
              className="pro-sa-btn pro-sa-btn--primary pro-sa-btn--block"
              onClick={openPurchaseModal}
            >
              Purchase More Service Areas
            </button>
            <p className="pro-sa-purchase__note">
              Each additional service area is ${PRICE_PER_AREA}/month. Choose a
              package or custom amount at checkout.
            </p>
          </section>
        )}

        {selectedArea && (
          <section className="pro-sa-map-section">
            <p className="pro-sa-map-section__label">
              <MapPin size={14} aria-hidden />
              Showing Service Area in
            </p>
            <h4>{formatAreaLabel(selectedArea)}</h4>
            <div className="pro-sa-map-section__map">
              <MapComponent
                placeId={selectedArea.placeId || selectedArea.place_id || ""}
                lat={Number(selectedArea.latitude)}
                lng={Number(selectedArea.longitude)}
              />
            </div>
            <p>Area surrounding your selected location</p>
          </section>
        )}

        {showPaymentRetry && (
          <div className="pro-sa-retry">
            <p>
              Your last payment for service area subscription failed. Please
              try again to continue without interruption.
            </p>
            <button
              type="button"
              className="pro-sa-btn pro-sa-btn--primary"
              onClick={handleRetryPayment}
              disabled={checkoutLoading}
            >
              Try Again
            </button>
          </div>
        )}

      </div>

      <SelectServiceAreaModal
        open={selectOpen}
        onClose={() => {
          if (!isSaving) {
            setSelectOpen(false);
            setActiveSlot(null);
          }
        }}
        onSelect={handleSelectArea}
        isSaving={isSaving}
      />

      {areaPendingDelete &&
        createPortal(
          <div
            className="pro-sa-payment-overlay"
            onClick={cancelDeleteArea}
            role="presentation"
          >
            <div
              className="pro-sa-confirm-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="pro-sa-delete-title"
            >
              <h3 id="pro-sa-delete-title">Remove service area?</h3>
              <p>
                Are you sure you want to remove{" "}
                <strong>{formatAreaLabel(areaPendingDelete)}</strong>? This
                action cannot be undone.
              </p>
              <div className="pro-sa-confirm-modal__actions">
                <button
                  type="button"
                  className="pro-sa-btn pro-sa-btn--ghost"
                  onClick={cancelDeleteArea}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pro-sa-btn pro-sa-btn--danger"
                  onClick={deleteArea}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Removing..." : "Remove"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {showPaymentModal &&
        createPortal(
        <div
          className="pro-sa-payment-overlay"
          onClick={() => setShowPaymentModal(false)}
          role="presentation"
        >
          <div
            className="pro-sa-payment-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              className="pro-sa-payment-modal__close"
              onClick={() => setShowPaymentModal(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h3>Manage Service Areas</h3>
            <p className="pro-sa-payment-modal__plan">
              Current plan: You have <strong>{areas.length}</strong> service{" "}
              {areas.length === 1 ? "area" : "areas"}
            </p>
            <h4>Add More Service Areas</h4>
            <p className="pro-sa-payment-modal__subtitle">
              Select how many additional service areas you&apos;d like to add.
              Each area is ${PRICE_PER_AREA}/month.
            </p>

            <div className="pro-sa-payment-modal__cards">
              {AREA_PACKAGES.map((pkg) => (
                <button
                  key={pkg.id}
                  type="button"
                  className={[
                    "pro-sa-payment-card",
                    selectedPkg?.id === pkg.id && "is-active",
                    pkg.popular && "is-popular",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => {
                    setSelectedPkg(pkg);
                    setCustomAreas(pkg.areas);
                  }}
                >
                  {pkg.popular && <span className="pro-sa-payment-card__tag">POPULAR</span>}
                  <strong>{pkg.areas}</strong>
                  <span>Service Areas</span>
                  <em>${pkg.price}</em>
                  <small>per month</small>
                </button>
              ))}
            </div>

            <div className="pro-sa-payment-modal__custom">
              <h5>Custom Amount</h5>
              <div className="pro-sa-payment-modal__stepper">
                <button
                  type="button"
                  onClick={() =>
                    setCustomAreas((prev) => (prev > 1 ? prev - 1 : 1))
                  }
                >
                  −
                </button>
                <span>
                  <strong>{customAreas}</strong>
                  areas
                </span>
                <button
                  type="button"
                  onClick={() => setCustomAreas((prev) => prev + 1)}
                >
                  +
                </button>
                <span>
                  <strong>${customAreas * PRICE_PER_AREA}</strong>
                  per month
                </span>
              </div>
            </div>

            <button
              type="button"
              className="pro-sa-btn pro-sa-btn--primary pro-sa-btn--block"
              onClick={handleCheckout}
              disabled={checkoutLoading}
            >
              Purchase {customAreas} Additional Areas
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

const ServiceAreas = (props) => (
  <GoogleMapsPlacesProvider>
    <ServiceAreasInner {...props} />
  </GoogleMapsPlacesProvider>
);

export default ServiceAreas;
